import { Value } from 'sma.admin/components/filters/values';
import PredicateTypes from 'sma.admin/components/filters/types';
import DropSelectorDrilldown from 'sma.admin/components/dropdowns/dropselectordrilldown';
import DropSelector from 'sma.admin/components/dropdowns/dropselector';


class ConnectiveNode {
	constructor (filters, info) {
		this.nodeType = 'Connective',

		this.negated = false;
		this.type = 'AND';
		this.isRoot = false;
		this.filters = filters;

		this.parent = null;

		this.children = [];

		this.buildElement();

		if (info !== undefined) {
			this.loadFromInfo(info);
		}

		this.delegate();
	}

	loadFromInfo (info) {
		this.isRoot = info.isRoot;

		this.type = info.type || 'AND';
		this.negated = info.negated || false;
		this.element.find('select.node-type').val(this.type);
	}

	buildElement () {
		this.element = $(ConnectiveNode.BaseTemplate());
		this.element.find('select.node-type').val(this.type);
	}

	setParent (parentNode) { //This will not reset the structure, use this only on init
		this.parent = parentNode;
	}

	addNode (node) {
		node.setParent(this);
		this.children.push(node);
		this.element.children('.contents').append(node.element);

		if (this.isRoot) {
			this.element.children('.contents').append($('<hr>'));
		}
	}

	newNode () {
		const node = new VariableNode(this.filters);
		this.addNode(node);
	}

	nestNode (node) {
		if (this.children.length <= 1) { //Don't allow nesting on only 1 rule
			return;
		}

		const newConnective = new ConnectiveNode(this.filters);
		const nodeIndex = this.children.indexOf(node);

		this.children.splice(nodeIndex, 1);

		node.element.before(newConnective.element);

		newConnective.addNode(node);
		newConnective.setParent(this);

		//Make a new node, cause it only makes sense to nest with multiple rules
		newConnective.newNode();
		this.children.splice(nodeIndex, 0, newConnective);
	}

	spliceNode (node, beforeNode) {
		//Inserts the node before the 'beforeNode' node
		//Doesn't handle element detaching
		const beforeIndex = this.children.indexOf(beforeNode);

		if (beforeIndex < 0) {
			return;
		}

		this.children.splice(beforeIndex, 0, node);
		node.setParent(this);

		beforeNode.element.before(node.element);

		if (this.isRoot) {
			beforeNode.element.before($('<hr>'));
		}
	}

	removeNode (node, force) {
		if (force === undefined) {
			force = false;
		}

		const nodeIndex = this.children.indexOf(node);

		this.children.splice(nodeIndex, 1);

		if (this.isRoot) {
			node.element.next('hr:first').remove();
		}

		node.element.remove();

		if (!force && this.isRoot && this.variableNodes().length < 1) {
			this.newNode();
			return;
		}

		if (this.variableNodes().length == 1 && this.parent !== null) {
			//Only 1 var left in this nested non-root connective, collapse and give the parent the variable node
			this.parent.spliceNode(this.variableNodes()[0], this);
			this.children.splice(0,1);
			this.children.length = 0;
		}

		if (this.children.length == 0 && this.parent !== null) {
			this.parent.removeNode(this, true);
		}
	}

	variableNodes () {
		return this.children.filter(function(val) {
			return !!(val.nodeType === 'Variable');
		});
	}

	delegate () {
		this.element.on('change', 'select.node-type', $.proxy(function() {
			this.type = this.element.find('select.node-type').val();
		}, this));
	}

	stringify () {
		const childrenStrings = [];
		for (let i = 0; i < this.children.length; i++) {
			childrenStrings.push(this.children[i].stringify());
		}

		return {
			children: childrenStrings,
			connector: (this.type == 'AND')? 'and' : 'or'
		};
	}

	serialize () {
		const items = [];

		for (let i = 0; i < this.children.length; i++) {
			const serialized = this.children[i].serialize();
			if (serialized !== undefined && serialized !== null) {
				items.push(serialized);
			}
		}

		const obj = {
			type: this.type,
			items,
		};

		if (this.negated) {
			obj['negated'] = true;
		}

		return obj;
	}
}

ConnectiveNode.BaseTemplate = _.template("\
<div class='connective-node nested'>\
	<div class='type-container'>\
		"+gettext('Conditions to match')+"&nbsp;&nbsp;<select class='node-type form-control custom-select'>\
			<option value='AND'>" + gettext('All') + "</option>\
			<option value='OR'>" + gettext('Any') + "</option>\
		</select>\
	</div>\
	<div class='contents'>\
	</div>\
</div>\
");

class VariableNode {
	constructor (filters, filterInfo, info) {
		this.nodeType = 'Variable';
		this.parent = null;
		this.filters = filters;

		this.variable = null;
		this.predicate = null;
		this.value = null;  //This becomes an instance of a class
		/*
		* this._value - Used for initially loading from a previous serialized filter
		* Do not use this ever, it will not change once set, instead use
		* this.value.value
		*/
		this._value = null;
		this.filterInfo = filterInfo;

		if (info !== undefined) {
			this.loadFromInfo(info);
		}

		this.buildElement(this.filters.sourceDropdown);
	}

	setParent (parentNode) {  // This will not reset the structure, use this only on init
		this.parent = parentNode;
	}

	loadFromInfo (info) {
		this.variable = info.variable || null;
		this.predicate = info.predicate || null;
		this._value = info.value || null;
	}

	buildElement (sourceDropdown) {
		let tools = VariableNode.ToolsTemplate();
		let element = $(VariableNode.BaseTemplate({tools: tools}));

		this.sourceDropdown = sourceDropdown.clone();
		this.dropSelect = new DropSelectorDrilldown({
			fullWidth: true,
			dropdownToggle: true,
			buttonSize: 'btn-sm',
			$selectEl: this.sourceDropdown
		});

		this.variableElement = element.find('.variable');
		this.variableElement.append(this.dropSelect.wrapped);

		this.predicateElement = element.find('.predicate');
		this.valueElement = element.find('.value');

		this.predicateElement.empty();
		this.valueElement.empty();

		this.element = element;

		this.delegate();

		if (this.variable !== null) {
			this.dropSelect.val(this.variable);
			if (this.filterInfo !== undefined) {
				this.rebuildPredicate(true);
			}
		}
	}

	delegate () {
		this.sourceDropdown.on('change', $.proxy(this.variableChanged, this));
		this.predicateElement.on('change', 'select', $.proxy(this.predicateChanged, this));

		this.element.on('click', '.node-tools .add-rule', $.proxy(function(e) {
			e.preventDefault();
			this.parent.newNode();
		}, this));

		this.element.on('click', '.node-tools .nest-rule', $.proxy(function(e) {
			e.preventDefault();
			this.parent.nestNode(this);
		}, this));

		this.element.on('click', '.node-tools .remove-rule', $.proxy(function(e) {
			e.preventDefault();
			this.parent.removeNode(this);
			this.predicateDeleted(e);
		}, this));
	}

	variableChanged (_e) {
		this.variable = this.sourceDropdown.val();

		this.filterInfo = this.filters.idMap[this.variable];
		this.predicate = null;

		if (this.filterInfo !== undefined) {
			this.rebuildPredicate();
		}
	}

	rebuildPredicate (isInitial) {
		this.predicateElement.empty();
		let typeInfo = [];

		if (this.filterInfo.type.indexOf('+') > -1) {
			const splitTypes = this.filterInfo.type.split('+');

			for (let splitType of splitTypes) {
				typeInfo = typeInfo.concat(PredicateTypes[splitType]);
			}
		} else if (this.filterInfo.type in PredicateTypes) {
			typeInfo = PredicateTypes[this.filterInfo.type];
		}

		let dropdown = $('<select></select>', { class: 'predicate-selector' });

		for (let i = 0; i < typeInfo.length; i++) {
			dropdown.append(
				$('<option></option>', {
					value: typeInfo[i].value,
					text: typeInfo[i].label
				})
			);
		}

		const dropselector = new DropSelector({
			fullWidth: true,
			dropdownToggle: true,
			buttonSize: 'btn-sm',
			$selectEl: dropdown
		});

		this.predicateElement.append(dropselector.wrapped);

		if (this.predicate !== null) {
			dropselector.val(this.predicate);
		} else {
			const firstVal = dropdown.find('option:first').attr('value');
			dropselector.val(firstVal);
			this.predicate = firstVal;
		}

		this.predicateChanged(null, isInitial);

		this.predicateElement.append(dropselector.wrapped);
		if (this.value && this.value.hasOwnProperty('noPredicate') && this.value.noPredicate) {
			this.predicateElement.hide().css('display', 'none');
			this.valueElement.addClass('sma-value-fullwidth');
		} else {
			this.predicateElement.show().css('display', 'inline-block');
			this.valueElement.removeClass('sma-value-fullwidth');
		}
	}

	getPredicateType (val) {
		let typeInfo = [];
		if (this.filterInfo === undefined) {
			return null;
		}
		if (this.filterInfo.type.indexOf('+') > -1) {
			const splitTypes = this.filterInfo.type.split('+');

			for (let splitType of splitTypes) {
				typeInfo = typeInfo.concat(PredicateTypes[splitType]);
			}
		} else if (this.filterInfo.type in PredicateTypes) {
			typeInfo = PredicateTypes[this.filterInfo.type];
		}

		for (let i = 0; i < typeInfo.length; i++) {
			if (val === typeInfo[i].value) {
				return typeInfo[i].type;
			}
		}

		return null;
	}

	predicateDeleted(e) {
		if (this.parent && this.parent.filters && this.parent.filters.handleChanges) {
			this.parent.filters.handleChanges(this.parent);
		}
	}

	predicateChanged(e, isInitial) {
		if (isInitial !== true && this.parent && this.parent.filters && this.parent.filters.handleChanges) {
			this.parent.filters.handleChanges(this.parent);
		}
		this.valueElement.empty();

		this.predicate = this.predicateElement.find('select').val();

		const type = this.getPredicateType(this.predicateElement.find('select').val());
		const valueClass = Value.TypeMap[type];

		if (valueClass === null || valueClass === undefined) {
			this.value = null;
			this.valueElement.empty();
			return;
		} else {
			this.value = new valueClass(this.filterInfo);
		}

		this.valueElement.append(this.value.getElement());

		if (type !== 'datetime') {
			this.value.afterAppended();
		}

		if (isInitial && this.value !== null && this._value !== null) {
			this.value.set(this._value); //Set Default
		}

		if (type === 'datetime') {
			this.value.afterAppended();
		}

		this.valueElement.show().css('display', 'inline-block');
	}

	stringify () {
		let val = this.valueElement.find('option:selected').text();

		if (this.value !== null && $.isArray(this.value.value)) {
			if (this.value.value.length <= 3) {
				val = $.map(this.valueElement.find('option:selected'), function(j) { return $(j).text(); }).join(', ');
			} else {
				val = this.value.value.length + gettext(' Selected');
			}
		}

		return 'If <span class="var">'
			+ this.variableElement.find('.rr-ds-disp').text().replace('...', '').toLowerCase() + '</span> '
			+ '<span class="predicate">'
			+ this.predicateElement.find('option:selected').text().toLowerCase() + '</span> '
			+ '<span class="value">' + val + '</span>';
	}

	serialize () {
		const type = this.getPredicateType(this.predicateElement.find('select').val());

		if (type === null) {
			return;
		}

		const obj = {
			variable: this.variable,
			predicate: this.predicate,
			type,
		};

		if (type !== 'noop') {
			obj['value'] = this.value.value;
		}

		return obj;
	}
}

VariableNode.BaseTemplate = _.template("\
<div class='variable-node'>\
	<div class='variable'></div>\
	<div class='predicate'></div>\
	<div class='value'></div>\
	<%= tools %>\
</div>\
");

VariableNode.ToolsTemplate = _.template("\
<div class='node-tools'>\
	<a href='#' class='add-rule' title='"+gettext('Add rule below')+"'><i class='nc-icon-mini ui-1_bold-add'></i></a>\
	<a href='#' class='remove-rule' title='"+gettext('Remove rule')+"'><i class='nc-icon-mini ui-1_trash-simple'></i></a>\
	<a href='#' class='nest-rule' title='"+gettext('Nest rule')+"'><i class='nc-icon-mini arrows-1_redo-79'></i></a>\
</div>\
");

module.exports = {
	VariableNode: VariableNode,
	ConnectiveNode: ConnectiveNode,
};
